import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TRANSCRIPT_SYNC_INTERVAL_MS } from "./constants";
import { clearTranscriptDraft, saveTranscriptDraft } from "./transcriptDraftStore";
import {
  createInitialSyncState,
  nextDelayMs,
  shouldSync,
  type SyncState,
} from "./transcript-sync-policy";

export type TranscriptSyncStatus = "idle" | "saving" | "saved" | "offline";

interface UseTranscriptSyncParams {
  sessionIdRef: React.RefObject<string | null>;
  transcript: string;
  isCapturing: boolean;
}

const FINALIZE_MAX_ATTEMPTS = 3;
const FINALIZE_RETRY_DELAY_MS = 800;

export const useTranscriptSync = ({
  sessionIdRef,
  transcript,
  isCapturing,
}: UseTranscriptSyncParams) => {
  const [syncStatus, setSyncStatus] = useState<TranscriptSyncStatus>("idle");
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const syncStateRef = useRef<SyncState>(createInitialSyncState());
  const transcriptRef = useRef(transcript);
  const timerRef = useRef<number | null>(null);

  transcriptRef.current = transcript;

  const syncNow = useCallback(async (): Promise<boolean> => {
    const transcriptionId = sessionIdRef.current;
    const candidate = transcriptRef.current.trim();
    if (!transcriptionId) return false;

    // Camada local primeiro: nao depende de rede e sobrevive a queda/reload.
    if (candidate) {
      await saveTranscriptDraft(transcriptionId, candidate);
    }

    if (!shouldSync(syncStateRef.current, candidate)) return false;

    syncStateRef.current = { ...syncStateRef.current, inFlight: true };
    setSyncStatus("saving");

    try {
      const { error } = await supabase
        .from("consultation_transcriptions")
        .update({ transcript: candidate, auto_saved_at: new Date().toISOString() })
        .eq("id", transcriptionId)
        .eq("status", "draft");

      if (error) throw error;

      syncStateRef.current = {
        lastSyncedTranscript: candidate,
        inFlight: false,
        consecutiveFailures: 0,
      };
      setLastSyncedAt(new Date());
      setSyncStatus("saved");
      return true;
    } catch (syncError) {
      console.warn("[transcription] sync:failed", syncError);
      syncStateRef.current = {
        ...syncStateRef.current,
        inFlight: false,
        consecutiveFailures: syncStateRef.current.consecutiveFailures + 1,
      };
      setSyncStatus("offline");
      return false;
    }
  }, [sessionIdRef]);

  /**
   * Congela o transcript e finaliza a captura numa unica operacao atomica.
   * E o unico write que nao pode falhar, entao tem retry proprio e agressivo.
   */
  const finalizeTranscript = useCallback(
    async (finalTranscript: string): Promise<boolean> => {
      const transcriptionId = sessionIdRef.current;
      const candidate = finalTranscript.trim();
      if (!transcriptionId || !candidate) return false;

      await saveTranscriptDraft(transcriptionId, candidate);

      for (let attempt = 1; attempt <= FINALIZE_MAX_ATTEMPTS; attempt++) {
        const { error } = await supabase
          .from("consultation_transcriptions")
          .update({
            transcript: candidate,
            status: "finalized",
            auto_saved_at: new Date().toISOString(),
          })
          .eq("id", transcriptionId)
          .eq("status", "draft");

        if (!error) {
          syncStateRef.current = {
            lastSyncedTranscript: candidate,
            inFlight: false,
            consecutiveFailures: 0,
          };
          setLastSyncedAt(new Date());
          setSyncStatus("saved");
          await clearTranscriptDraft(transcriptionId);
          return true;
        }

        console.warn("[transcription] finalize:attemptFailed", { attempt, error });
        if (attempt < FINALIZE_MAX_ATTEMPTS) {
          await new Promise((resolve) => window.setTimeout(resolve, FINALIZE_RETRY_DELAY_MS * attempt));
        }
      }

      setSyncStatus("offline");
      return false;
    },
    [sessionIdRef],
  );

  useEffect(() => {
    if (!isCapturing) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const schedule = (delayMs: number) => {
      timerRef.current = window.setTimeout(async () => {
        if (cancelled) return;
        await syncNow();
        if (cancelled) return;
        schedule(nextDelayMs(syncStateRef.current.consecutiveFailures));
      }, delayMs);
    };

    schedule(TRANSCRIPT_SYNC_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isCapturing, syncNow]);

  return { syncStatus, lastSyncedAt, syncNow, finalizeTranscript };
};
